import {
  Injectable,
  OnDestroy,
  Signal,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterKey } from '@wilson/filter';
import { Subject, takeUntil } from 'rxjs';

enum TimelineFilterKeys {
  OrgUnit = 'orgUnit',
  ShiftOrgUnit = 'shiftOrgUnit',
  Labels = 'labels',
  ShiftLabels = 'shiftLabels',
  Profession = 'profession',
  ShiftProfession = 'shiftProfession',
}

@Injectable({
  providedIn: 'root',
})
export class TimelineFilterHelperService implements OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private destroy$ = new Subject<void>();

  private showCopyOrgUnitFromShiftSignal = signal<boolean>(false);
  private showCopyOrgUnitFromUserSignal = signal<boolean>(false);
  private showCopyLabelFromShiftSignal = signal<boolean>(false);
  private showCopyLabelFromUserSignal = signal<boolean>(false);
  private showCopyProfessionFromShiftSignal = signal<boolean>(false);
  private showCopyProfessionFromUserSignal = signal<boolean>(false);

  constructor() {
    this.initializeVisibilityChecks();
  }

  public get isCopyOrgUnitFromShiftVisibleSignal(): Signal<boolean> {
    return this.showCopyOrgUnitFromShiftSignal.asReadonly();
  }

  public get isCopyOrgUnitFromUserVisibleSignal(): Signal<boolean> {
    return this.showCopyOrgUnitFromUserSignal.asReadonly();
  }

  public get isCopyLabelFromShiftVisibleSignal(): Signal<boolean> {
    return this.showCopyLabelFromShiftSignal.asReadonly();
  }

  public get isCopyLabelFromUserVisibleSignal(): Signal<boolean> {
    return this.showCopyLabelFromUserSignal.asReadonly();
  }

  public get isCopyProfessionFromShiftVisibleSignal(): Signal<boolean> {
    return this.showCopyProfessionFromShiftSignal.asReadonly();
  }

  public get isCopyProfessionFromUserVisibleSignal(): Signal<boolean> {
    return this.showCopyProfessionFromUserSignal.asReadonly();
  }

  public copyField(fromField: FilterKey, toField: FilterKey): void {
    const params = this.activatedRoute.snapshot.queryParams;
    const fromValue = params[fromField];
    this.updateQueryParam(toField, fromValue);
  }

  private initializeVisibilityChecks(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.updateVisibility(
          params[TimelineFilterKeys.OrgUnit],
          params[TimelineFilterKeys.ShiftOrgUnit],
          this.showCopyOrgUnitFromUserSignal,
          this.showCopyOrgUnitFromShiftSignal,
        );

        this.updateVisibility(
          params[TimelineFilterKeys.Labels],
          params[TimelineFilterKeys.ShiftLabels],
          this.showCopyLabelFromUserSignal,
          this.showCopyLabelFromShiftSignal,
        );

        this.updateVisibility(
          params[TimelineFilterKeys.Profession],
          params[TimelineFilterKeys.ShiftProfession],
          this.showCopyProfessionFromUserSignal,
          this.showCopyProfessionFromShiftSignal,
        );
      });
  }

  private updateVisibility(
    userParam: string | undefined,
    shiftParam: string | undefined,
    userSignal: WritableSignal<boolean>,
    shiftSignal: WritableSignal<boolean>,
  ): void {
    const userValues = this.parseParam(userParam || '');
    const shiftValues = this.parseParam(shiftParam || '');
    const areSame = this.arraysEqual(userValues, shiftValues);

    if (!areSame) {
      const shouldShowCopyFromUser =
        shiftValues.length > 0 && userValues.length === 0;
      const shouldShowCopyFromShift =
        userValues.length > 0 && shiftValues.length === 0;
      const shouldShowBoth = userValues.length > 0 && shiftValues.length > 0;

      userSignal.set(shouldShowCopyFromShift || shouldShowBoth);
      shiftSignal.set(shouldShowCopyFromUser || shouldShowBoth);
    } else {
      userSignal.set(false);
      shiftSignal.set(false);
    }
  }

  private updateQueryParam(field: FilterKey, value: string): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [field]: value ? value : null },
      queryParamsHandling: 'merge',
    });
  }

  private parseParam(param: string): string[] {
    return param ? param.split(',').sort() : [];
  }

  private arraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    return arr1.every((val, index) => val === arr2[index]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
